<app-dialog>
  <app-dialog-title>{{ data.title }}</app-dialog-title>

  <app-dialog-content>
    <div class="translate-dialog">
      @if (data.infoMessage) {
        <app-info-form-box>{{ data.infoMessage }}</app-info-form-box>
      }

      <div class="translate-dialog__summary">
        <div
          class="translate-dialog__progress-fill"
          [style.width.%]="totalCount() > 0 ? (completedCount() / totalCount()) * 100 : 0"
          [class.translate-dialog__progress-fill--complete]="
            completedCount() === totalCount()
          "></div>
        <mat-icon class="translate-dialog__summary-icon" aria-hidden="true">language</mat-icon>
        <span class="translate-dialog__summary-text">
          {{
            'SHARED.TRANSLATE_DIALOG.PROGRESS'
              | translate
                : {
                    complete: completedCount(),
                    total: totalCount(),
                    plural: totalCount() !== 1 ? 's' : '',
                  }
          }}
        </span>
        @if (completedCount() === totalCount() && totalCount() > 0) {
          <mat-icon
            class="translate-dialog__complete-icon"
            [attr.aria-label]="'SHARED.TRANSLATE_DIALOG.STATUS_COMPLETE' | translate"
            >check_circle</mat-icon
          >
        }
      </div>

      @if (isMultiField()) {
        <!-- Focus-rail layout: one language at a time, source travels with each field -->
        <div class="translate-dialog__rail-layout">
          <nav class="translate-dialog__rail" aria-label="Languages">
            @if (data.defaultLanguage) {
              <div class="translate-dialog__rail-group-header">
                <span class="translate-dialog__rail-group-label">{{
                  'SHARED.TRANSLATE_DIALOG.DEFAULT_GROUP_LABEL' | translate
                }}</span>
              </div>
              <div class="translate-dialog__rail-item translate-dialog__rail-item--default">
                <span
                  class="flag-icon flag-icon-round translate-dialog__rail-flag"
                  [class]="'flag-icon-' + data.defaultLanguage.code"
                  aria-hidden="true"></span>
                <span class="translate-dialog__rail-item-name">{{
                  data.defaultLanguage.name ?? data.defaultLanguage.code
                }}</span>
                <span class="translate-dialog__rail-item-sub">{{
                  'SHARED.TRANSLATE_DIALOG.DEFAULT_LANG_SUBLABEL' | translate
                }}</span>
              </div>
            }

            <div class="translate-dialog__rail-group-header">
              <span class="translate-dialog__rail-group-label">{{
                'SHARED.TRANSLATE_DIALOG.ACTIVE_LANGUAGES_LABEL' | translate
              }}</span>
              <span class="translate-dialog__rail-count">{{ data.languages.length }}</span>
            </div>
            <ul class="translate-dialog__rail-list">
              @for (lang of data.languages; track lang.code) {
                <li>
                  <button
                    type="button"
                    class="translate-dialog__rail-item"
                    [class.translate-dialog__rail-item--selected]="selectedLangCode() === lang.code"
                    [disabled]="isSuggesting()"
                    (click)="selectedLangCode.set(lang.code)"
                    [attr.aria-current]="selectedLangCode() === lang.code ? 'true' : null">
                    <span
                      class="flag-icon flag-icon-round translate-dialog__rail-flag"
                      [class]="'flag-icon-' + lang.code"
                      aria-hidden="true"></span>
                    <span class="translate-dialog__rail-item-name">{{
                      lang.name ?? lang.code
                    }}</span>
                    @if (completionStateMap()[lang.code] === 'ok') {
                      <span class="translate-dialog__status-ring" aria-hidden="true">
                        <mat-icon>check</mat-icon>
                      </span>
                    } @else {
                      <span
                        class="translate-dialog__status-dot"
                        [class.translate-dialog__status-dot--partial]="
                          completionStateMap()[lang.code] === 'partial'
                        "
                        [class.translate-dialog__status-dot--none]="
                          completionStateMap()[lang.code] === 'none'
                        "
                        [attr.aria-label]="
                          completionStateMap()[lang.code] === 'partial'
                            ? ('SHARED.TRANSLATE_DIALOG.STATUS_PARTIAL' | translate)
                            : null
                        "></span>
                    }
                  </button>
                </li>
              }
            </ul>

            @if ((data.inactiveLanguages?.length ?? 0) > 0) {
              <button
                type="button"
                class="translate-dialog__rail-group-header translate-dialog__rail-group-header--toggle"
                (click)="inactiveExpanded.set(!inactiveExpanded())"
                [attr.aria-expanded]="inactiveExpanded()"
                data-testid="btn-toggle-inactive-languages">
                <span class="translate-dialog__rail-group-label">{{
                  'SHARED.TRANSLATE_DIALOG.INACTIVE_GROUP_LABEL' | translate
                }}</span>
                <span class="translate-dialog__rail-count">{{
                  data.inactiveLanguages?.length
                }}</span>
                <mat-icon
                  class="translate-dialog__chevron"
                  [class.translate-dialog__chevron--open]="inactiveExpanded()"
                  aria-hidden="true"
                  >expand_more</mat-icon
                >
              </button>
              @if (inactiveExpanded()) {
                <ul class="translate-dialog__rail-list">
                  @for (lang of data.inactiveLanguages; track lang.code) {
                    <li>
                      <button
                        type="button"
                        class="translate-dialog__rail-item translate-dialog__rail-item--dim"
                        [class.translate-dialog__rail-item--selected]="
                          selectedLangCode() === lang.code
                        "
                        [disabled]="isSuggesting()"
                        (click)="selectedLangCode.set(lang.code)"
                        [attr.aria-current]="selectedLangCode() === lang.code ? 'true' : null">
                        <span
                          class="flag-icon flag-icon-round translate-dialog__rail-flag"
                          [class]="'flag-icon-' + lang.code"
                          aria-hidden="true"></span>
                        <span class="translate-dialog__rail-item-name">{{
                          lang.name ?? lang.code
                        }}</span>
                        @if (completionStateMap()[lang.code] === 'ok') {
                          <span class="translate-dialog__status-ring" aria-hidden="true">
                            <mat-icon>check</mat-icon>
                          </span>
                        } @else {
                          <span
                            class="translate-dialog__status-dot"
                            [class.translate-dialog__status-dot--partial]="
                              completionStateMap()[lang.code] === 'partial'
                            "
                            [class.translate-dialog__status-dot--none]="
                              completionStateMap()[lang.code] === 'none'
                            "
                            aria-hidden="true"></span>
                        }
                      </button>
                    </li>
                  }
                </ul>
              }
            }
          </nav>

          <!-- Translation pane for the selected language -->
          <div
            class="translate-dialog__pane"
            role="region"
            [attr.aria-label]="selectedLang()?.name">
            @if (selectedLang(); as lang) {
              <header class="translate-dialog__pane-header">
                <span
                  class="flag-icon flag-icon-round translate-dialog__pane-flag"
                  [class]="'flag-icon-' + lang.code"
                  aria-hidden="true"></span>
                <div class="translate-dialog__pane-title">
                  <span class="translate-dialog__pane-name">{{ lang.name ?? lang.code }}</span>
                  <span class="translate-dialog__pane-meta">
                    {{
                      'SHARED.TRANSLATE_DIALOG.FIELDS_TRANSLATED'
                        | translate
                          : {
                              done: selectedLangFieldsDone(),
                              total: requiredFieldCount(),
                            }
                    }}
                  </span>
                </div>
                <span
                  class="translate-dialog__chip"
                  [class.translate-dialog__chip--active]="isActiveLang(lang.code)"
                  [class.translate-dialog__chip--inactive]="!isActiveLang(lang.code)">
                  {{
                    isActiveLang(lang.code)
                      ? ('SHARED.TRANSLATE_DIALOG.CHIP_ACTIVE' | translate)
                      : ('SHARED.TRANSLATE_DIALOG.CHIP_INACTIVE' | translate)
                  }}
                </span>
                <span
                  class="translate-dialog__ai-btn-wrap"
                  [appAiUpsellPopover]="!isAiEnabled()"
                  [attr.tabindex]="isAiEnabled() ? null : 0"
                  [attr.aria-label]="
                    isAiEnabled()
                      ? null
                      : ('SHARED.TRANSLATE_DIALOG.AI_REQUIRES_INTELLIGENCE' | translate)
                  ">
                  <button
                    type="button"
                    class="translate-dialog__ai-btn"
                    [class.translate-dialog__ai-btn--loading]="isSuggesting()"
                    [disabled]="!canSuggest() || isSuggesting() || !isAiEnabled()"
                    (click)="suggestAll([selectedLangCode()])"
                    [attr.aria-label]="'SHARED.TRANSLATE_DIALOG.AI_SUGGEST_ALL' | translate">
                    @if (isSuggesting()) {
                      <span class="translate-dialog__ai-spinner" aria-hidden="true"></span>
                    } @else {
                      <span class="translate-dialog__ai-glyph" aria-hidden="true"></span>
                    }
                    {{
                      isSuggesting()
                        ? ('SHARED.TRANSLATE_DIALOG.AI_SUGGESTING' | translate)
                        : ('SHARED.TRANSLATE_DIALOG.AI_SUGGEST_ALL' | translate)
                    }}
                  </button>
                </span>
              </header>

              @if (!isActiveLang(lang.code)) {
                <div class="translate-dialog__ahead-banner" role="note">
                  <mat-icon class="translate-dialog__ahead-icon" aria-hidden="true"
                    >schedule</mat-icon
                  >
                  <p class="translate-dialog__ahead-text">
                    <strong>{{ lang.name ?? lang.code }}</strong>
                    {{ 'SHARED.TRANSLATE_DIALOG.INACTIVE_BANNER' | translate }}
                  </p>
                </div>
              }

              @if (isAiTranslated(lang.code)) {
                <app-ai-disclosure
                  kind="generative"
                  variant="note"
                  [testId]="'ai-disclosure-translation-' + lang.code" />
              }

              <div class="translate-dialog__pane-fields">
                @for (field of fields; track field.key) {
                  <div
                    class="translate-dialog__pane-field"
                    [class.translate-dialog__pane-field--translating]="isSuggesting()">
                    @if (field.multiline) {
                      <app-textarea-with-counter
                        [label]="field.label | translate: field.labelParams"
                        [maxLength]="field.maxLength"
                        [ngModel]="getValue(lang.code, field.key)"
                        (ngModelChange)="setValue(lang.code, field.key, $event)"
                        [attr.aria-label]="
                          (field.label | translate: field.labelParams) +
                          ' in ' +
                          (lang.name ?? lang.code)
                        "
                        [disabled]="isReadonlyField(lang.code, field.key) || isSuggesting()"
                        [attr.data-testid]="'input-translation-' + lang.code + '-' + field.key"
                        [minHeight]="72"
                        ngDefaultControl>
                        @if (sourceValues[field.key]) {
                          <p
                            class="translate-dialog__source-hint"
                            [appTooltipWhenTruncated]="sourceValues[field.key]">
                            <span class="translate-dialog__source-hint-label">{{
                              'SHARED.TRANSLATE_DIALOG.ORIGINAL_HINT_LABEL' | translate
                            }}</span>
                            - {{ sourceValues[field.key] }}
                          </p>
                        }
                      </app-textarea-with-counter>
                    } @else {
                      <app-input-with-counter
                        [label]="field.label | translate: field.labelParams"
                        [maxLength]="field.maxLength"
                        [ngModel]="getValue(lang.code, field.key)"
                        (ngModelChange)="setValue(lang.code, field.key, $event)"
                        [attr.aria-label]="
                          (field.label | translate: field.labelParams) +
                          ' in ' +
                          (lang.name ?? lang.code)
                        "
                        [disabled]="isReadonlyField(lang.code, field.key) || isSuggesting()"
                        [attr.data-testid]="'input-translation-' + lang.code + '-' + field.key"
                        ngDefaultControl>
                        @if (sourceValues[field.key]) {
                          <p
                            class="translate-dialog__source-hint translate-dialog__source-hint--inset"
                            [appTooltipWhenTruncated]="sourceValues[field.key]">
                            <span class="translate-dialog__source-hint-label">{{
                              'SHARED.TRANSLATE_DIALOG.ORIGINAL_HINT_LABEL' | translate
                            }}</span>
                            - {{ sourceValues[field.key] }}
                          </p>
                        }
                      </app-input-with-counter>
                    }
                    @if (failureFor(lang.code, field.key); as failure) {
                      <app-failed-translation-note
                        class="mt-2"
                        [failure]="failure"
                        [value]="getValue(lang.code, field.key)"
                        [fieldName]="field.label | translate: field.labelParams"
                        [disabled]="isReadonlyField(lang.code, field.key) || isSuggesting()"
                        [testId]="lang.code + '-' + field.key"
                        (useRejectedText)="setValue(lang.code, field.key, $event)" />
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      } @else {
        <!-- Stacked layout: all languages at once, via the shared language
             list (default language pinned read-only at the top). -->
        @if (fields[0]; as field) {
          <app-translation-language-list
            [defaultLanguage]="data.defaultLanguage"
            [languages]="data.languages"
            [inactiveLanguages]="data.inactiveLanguages ?? []"
            [defaultValue]="sourceValues[field.key] ?? ''"
            [values]="singleFieldValues()"
            (valuesChange)="onSingleFieldValuesChange($event)"
            [readonlyLangs]="singleFieldReadonlyLangs()"
            [fieldLabel]="field.label | translate: field.labelParams"
            [maxLength]="field.maxLength"
            [testIdSuffix]="field.key"
            [isSuggesting]="isSuggesting()"
            [canSuggest]="canSuggest()"
            [aiAvailable]="isAiEnabled()"
            [aiTranslatedCodes]="aiTranslatedCodes()"
            [failedTranslations]="singleFieldFailures()"
            (suggestAll)="suggestAll(allLangCodes())" />
        }
      }
    </div>
  </app-dialog-content>

  <app-dialog-actions>
    <button matButton="outlined" type="button" data-testid="btn-dialog-cancel" matDialogClose>
      {{ 'SHARED.TRANSLATE_DIALOG.CANCEL_BTN' | translate }}
    </button>
    <button
      matButton="filled"
      type="button"
      data-testid="btn-dialog-save"
      [disabled]="hasTextOverLimit()"
      (click)="save()">
      {{ 'SHARED.TRANSLATE_DIALOG.SAVE_BTN' | translate }}
    </button>
  </app-dialog-actions>
</app-dialog>
